import { supabase } from '@/lib/supabase'

export type FinanceCostCategory = {
  id: string
  name: string
  created_at: string
  updated_at: string
}

export type FinanceFixedCost = {
  id: string
  category_id: string | null
  name: string
  amount_cents: number
  currency: string
  start_month: string
  end_month: string | null
  notes: string | null
  created_at: string
  updated_at: string
}

export type FinanceExpense = {
  id: string
  category_id: string | null
  name: string
  amount_cents: number
  currency: string
  competence_month: string
  paid_at: string | null
  notes: string | null
  created_at: string
  updated_at: string
}

export async function listFinanceCostCategories(): Promise<FinanceCostCategory[]> {
  const { data, error } = await supabase
    .from('finance_cost_categories')
    .select('id,name,created_at,updated_at')
    .order('name', { ascending: true })
  if (error) throw error
  return (data || []) as any
}

export async function upsertFinanceCostCategory(payload: { id?: string; name: string }): Promise<void> {
  const name = String(payload.name || '').trim()
  if (!name) throw new Error('Nome é obrigatório')
  const row: any = { ...(payload.id ? { id: payload.id } : {}), name }
  const { error } = await supabase.from('finance_cost_categories').upsert(row, { onConflict: 'id' } as any)
  if (error) throw error
}

export async function deleteFinanceCostCategory(id: string): Promise<void> {
  const { error } = await supabase.from('finance_cost_categories').delete().eq('id', id)
  if (error) throw error
}

export async function listFinanceFixedCosts(params: { monthStartDate: string; showAll?: boolean }): Promise<FinanceFixedCost[]> {
  const { monthStartDate, showAll = false } = params
  let q = supabase
    .from('finance_fixed_costs')
    .select('id,category_id,name,amount_cents,currency,start_month,end_month,notes,created_at,updated_at')
    .order('start_month', { ascending: false })
    .order('name', { ascending: true })

  if (!showAll) {
    q = q.lte('start_month', monthStartDate).or(`end_month.is.null,end_month.gte.${monthStartDate}`)
  }

  const { data, error } = await q
  if (error) throw error
  return (data || []) as any
}

export async function upsertFinanceFixedCost(payload: {
  id?: string
  category_id?: string | null
  name: string
  amount_cents: number
  currency?: string
  start_month: string
  end_month?: string | null
  notes?: string | null
}): Promise<void> {
  const row: any = {
    ...(payload.id ? { id: payload.id } : {}),
    category_id: payload.category_id ?? null,
    name: String(payload.name || '').trim(),
    amount_cents: Math.max(0, Math.floor(Number(payload.amount_cents || 0))),
    currency: String(payload.currency || 'BRL').toUpperCase(),
    start_month: payload.start_month,
    end_month: payload.end_month ?? null,
    notes: payload.notes ?? null,
  }
  if (!row.name) throw new Error('Nome é obrigatório')
  if (!row.start_month) throw new Error('Início é obrigatório')
  const { error } = await supabase.from('finance_fixed_costs').upsert(row, { onConflict: 'id' } as any)
  if (error) throw error
}

export async function deleteFinanceFixedCost(id: string): Promise<void> {
  const { error } = await supabase.from('finance_fixed_costs').delete().eq('id', id)
  if (error) throw error
}

export async function listFinanceExpenses(params: { monthStartDate: string }): Promise<FinanceExpense[]> {
  const { monthStartDate } = params
  const { data, error } = await supabase
    .from('finance_expenses')
    .select('id,category_id,name,amount_cents,currency,competence_month,paid_at,notes,created_at,updated_at')
    .eq('competence_month', monthStartDate)
    .order('created_at', { ascending: false })
  if (error) throw error
  return (data || []) as any
}

export async function upsertFinanceExpense(payload: {
  id?: string
  category_id?: string | null
  name: string
  amount_cents: number
  currency?: string
  competence_month: string
  paid_at?: string | null
  notes?: string | null
}): Promise<void> {
  const row: any = {
    ...(payload.id ? { id: payload.id } : {}),
    category_id: payload.category_id ?? null,
    name: String(payload.name || '').trim(),
    amount_cents: Math.max(0, Math.floor(Number(payload.amount_cents || 0))),
    currency: String(payload.currency || 'BRL').toUpperCase(),
    competence_month: payload.competence_month,
    paid_at: payload.paid_at ?? null,
    notes: payload.notes ?? null,
  }
  if (!row.name) throw new Error('Nome é obrigatório')
  if (!row.competence_month) throw new Error('Competência é obrigatória')
  const { error } = await supabase.from('finance_expenses').upsert(row, { onConflict: 'id' } as any)
  if (error) throw error
}

export async function deleteFinanceExpense(id: string): Promise<void> {
  const { error } = await supabase.from('finance_expenses').delete().eq('id', id)
  if (error) throw error
}

